import { GET_CHAT_BY_ID } from '@/hooks/query/useQueryChatById';
import { GET_CHATS } from '@/hooks/query/useQueryChats';
import { GET_SAVED_CHATS } from '@/hooks/query/useQuerySavedChats';
import { logger } from '@/utils/logger/logger';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { projectsQueryKeys, type ProjectDetail } from '@heidi/agents/contracts';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import type { ChatListResponse } from '@heidi/session/contracts';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

export interface UpdateChatTitleVariables {
  chatId: string;
  title: string;
}

/** Max length for chat title; should match backend constraint. */
const CHAT_TITLE_MAX_LENGTH = 255;

/**
 * Sanitizes title for display and storage: trims, replaces control characters
 * and newlines with a space, and collapses repeated spaces.
 */
function sanitizeChatTitle(title: string): string {
  return title
    .replace(/[\x00-\x1f\x7f]+/g, ' ') // control chars and DEL
    .replace(/\s+/g, ' ')
    .trim();
}

function updateChatTitleInList(
  chats: ChatListResponse['chats'],
  chatId: string,
  title: string
) {
  return chats.map((chat) => (chat.id === chatId ? { ...chat, title } : chat));
}

/**
 * Updates a chat's title via PATCH /chat/chats/{chat_id}.
 * Expects backend to support PATCH with body: { title: string }.
 * Optimistically updates all chat list caches (paginated and infinite).
 */
interface MutationContext {
  previousPaginated: [readonly unknown[], ChatListResponse | undefined][];
  previousInfinite: [
    readonly unknown[],
    InfiniteData<ChatListResponse, number> | undefined,
  ][];
  previousSaved: [readonly unknown[], ChatListResponse | undefined][];
}

export function useMutateUpdateChatTitle() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();

  return useMutation<void, Error, UpdateChatTitleVariables, MutationContext>({
    mutationFn: async ({ chatId, title }) => {
      const sanitized = sanitizeChatTitle(title);
      if (!sanitized) {
        throw new Error('EMPTY_TITLE');
      }
      if (sanitized.length > CHAT_TITLE_MAX_LENGTH) {
        throw new Error('TITLE_TOO_LONG');
      }
      await scribeHttpV2.patch(`/chat/chats/${chatId}`, { title: sanitized });
    },
    onMutate: ({ chatId, title }) => {
      const sanitized = sanitizeChatTitle(title);
      if (!sanitized || sanitized.length > CHAT_TITLE_MAX_LENGTH) return;

      const previousPaginated = queryClient.getQueriesData<ChatListResponse>({
        queryKey: [GET_CHATS],
      });
      const previousInfinite = queryClient.getQueriesData<
        InfiniteData<ChatListResponse, number>
      >({
        queryKey: [GET_CHATS, 'infinite'],
      });
      const previousSaved = queryClient.getQueriesData<ChatListResponse>({
        queryKey: [GET_SAVED_CHATS],
      });

      const updatePaginated = (old: ChatListResponse | undefined) => {
        if (!old?.chats) return old;
        return { ...old, chats: updateChatTitleInList(old.chats, chatId, sanitized) };
      };
      const updateInfinite = (
        old: InfiniteData<ChatListResponse, number> | undefined
      ) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            chats: updateChatTitleInList(page.chats, chatId, sanitized),
          })),
        };
      };

      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: [GET_CHATS] },
        updatePaginated
      );
      queryClient.setQueriesData<InfiniteData<ChatListResponse, number>>(
        { queryKey: [GET_CHATS, 'infinite'] },
        updateInfinite
      );
      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: [GET_SAVED_CHATS] },
        updatePaginated
      );

      return { previousPaginated, previousInfinite, previousSaved };
    },
    onSuccess: (_data, { chatId, title }) => {
      const sanitized = sanitizeChatTitle(title);
      const detailKey = projectsQueryKeys.detail(chatId);
      const existing = queryClient.getQueryData<ProjectDetail>(detailKey);
      if (existing) {
        queryClient.setQueryData<ProjectDetail>(detailKey, {
          ...existing,
          title: sanitized,
          title_is_custom: true,
        });
      }
      void queryClient.invalidateQueries({ queryKey: [GET_CHAT_BY_ID, chatId] });
      void queryClient.invalidateQueries({ queryKey: [GET_SAVED_CHATS] });
      toast({
        title: t('chat.history.renameSuccessTitle'),
        tone: 'success',
      });
    },
    onError: (error, { chatId, title }, context) => {
      logger.error({ error, chatId, title });

      if (context) {
        for (const [key, data] of context.previousPaginated) {
          queryClient.setQueryData(key, data);
        }
        for (const [key, data] of context.previousInfinite) {
          queryClient.setQueryData(key, data);
        }
        for (const [key, data] of context.previousSaved) {
          queryClient.setQueryData(key, data);
        }
      }

      const isTooLong = error instanceof Error && error.message === 'TITLE_TOO_LONG';
      toast({
        title: isTooLong
          ? t('chat.history.renameErrorTitleTooLong')
          : t('chat.history.renameErrorTitle'),
        description: isTooLong
          ? t('chat.history.renameErrorDescriptionTooLong')
          : t('chat.history.renameErrorDescription'),
        tone: 'destructive',
      });
    },
  });
}
